import { useQuery } from '@tanstack/react-query'
import {
  Area,
  AreaChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { getBalanceHistory, type AccountSummary } from '@/api/accounts'
import { listValuations } from '@/api/valuations'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useIsDarkMode } from '@/hooks/useIsDarkMode'
import { formatShortMonth } from '@/lib/formatDate'
import { SectionCard, SectionHeader } from './AccountSections'

// Recharts takes plain colours, not CSS variables — these mirror --fl-primary, --fl-text-3,
// --fl-border, --fl-surface and --fl-text-1.
function chartColors(dark: boolean) {
  const c = dark
    ? { line: '#5aaa84', muted: '#8a8178', grid: '#3c3830', surface: '#2a2825', text: '#e4dfd8' }
    : { line: '#2d7a5f', muted: '#888888', grid: '#e0e0e0', surface: '#ffffff', text: '#111111' }
  return {
    ...c,
    tooltip: {
      contentStyle: {
        background: c.surface,
        border: `1px solid ${c.grid}`,
        borderRadius: 10,
        fontSize: 12,
      },
      labelStyle: { color: c.muted },
      itemStyle: { color: c.text },
    },
  }
}

/** Month-end balance (or, for a card, what was owed) over the last six months; hidden on error. */
export function BalanceChart({ account }: { account: AccountSummary }) {
  const { format } = useFormatCurrency()
  const colors = chartColors(useIsDarkMode())
  const { data, isError } = useQuery({
    queryKey: ['balance-history', account.id, 6],
    queryFn: () => getBalanceHistory(account.id, 6),
  })
  if (isError || !data || data.length === 0) return null
  const owed = account.type === 'CREDIT_CARD'
  const points = data.map((p) => ({
    month: formatShortMonth(p.month),
    value: owed ? Math.abs(p.balance) : p.balance,
  }))

  return (
    <section aria-label="Balance over time">
      <SectionHeader title={owed ? 'Owed · last 6 months' : 'Balance · last 6 months'} />
      <SectionCard className="px-3 pt-4 pb-2">
        <div className="h-[130px]" data-testid="balance-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 6, right: 10, bottom: 0, left: 10 }}>
              <defs>
                <linearGradient id="balance-fill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor={colors.line} stopOpacity={0.22} />
                  <stop offset="1" stopColor={colors.line} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                tick={{ fill: colors.muted, fontSize: 11 }}
              />
              <YAxis hide domain={['auto', 'auto']} />
              <Tooltip
                formatter={(v) => [format(Number(v)), owed ? 'Owed' : 'Balance']}
                cursor={{ stroke: colors.grid }}
                {...colors.tooltip}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={colors.line}
                strokeWidth={2.2}
                fill="url(#balance-fill)"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </SectionCard>
    </section>
  )
}

/**
 * Recorded values as dots joined by a line, beside what went in (the account balance) as a
 * dashed line. Property has no "put in" line — its balance is the frozen purchase price.
 */
export function ValueChart({ account }: { account: AccountSummary }) {
  const { format } = useFormatCurrency()
  const colors = chartColors(useIsDarkMode())
  const withPutIn = account.type === 'INVESTMENT'
  const { data: history, isError } = useQuery({
    queryKey: ['balance-history', account.id, 12],
    queryFn: () => getBalanceHistory(account.id, 12),
  })
  const { data: valuations } = useQuery({
    queryKey: ['valuations', account.id],
    queryFn: () => listValuations(account.id),
  })
  if (isError || !history || !valuations || valuations.length === 0) return null

  const points = history.map((p) => {
    // Newest first, so the first match is that month's latest recorded value.
    const value = valuations.find((v) => v.asOfDate.startsWith(p.month))
    return {
      month: formatShortMonth(p.month),
      worth: value?.currentValue ?? null,
      putIn: withPutIn ? p.balance : null,
    }
  })

  return (
    <section aria-label="Worth over time">
      <SectionHeader title={withPutIn ? 'Worth vs put in · 12 months' : 'Worth · 12 months'} />
      <SectionCard className="px-3 pt-3 pb-2">
        <div className="flex gap-4 px-2 pb-1 text-xs text-[var(--fl-text-2)]">
          <span className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-[3px] w-3.5 rounded bg-[var(--fl-primary)]" />
            Worth
          </span>
          {withPutIn && (
            <span className="flex items-center gap-1.5">
              <span aria-hidden="true" className="h-[3px] w-3.5 rounded bg-[var(--fl-text-3)]" />
              Put in
            </span>
          )}
        </div>
        <div className="h-[140px]" data-testid="value-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 6, right: 10, bottom: 0, left: 10 }}>
              <XAxis
                dataKey="month"
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
                tick={{ fill: colors.muted, fontSize: 11 }}
              />
              <YAxis hide domain={['auto', 'auto']} />
              <Tooltip
                formatter={(v, name) => [format(Number(v)), name === 'worth' ? 'Worth' : 'Put in']}
                cursor={{ stroke: colors.grid }}
                {...colors.tooltip}
              />
              {withPutIn && (
                <Line
                  type="monotone"
                  dataKey="putIn"
                  stroke={colors.muted}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  dot={false}
                />
              )}
              <Line
                type="monotone"
                dataKey="worth"
                stroke={colors.line}
                strokeWidth={2.4}
                connectNulls
                dot={{ r: 3, fill: colors.line }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <p className="px-2 pt-1 text-xs text-[var(--fl-text-3)]">
          Dots are the values you entered.
        </p>
      </SectionCard>
    </section>
  )
}

/** A tiny six-month trend for the hero on a phone; the full chart sits further down the page. */
export function BalancePreview({ account }: { account: AccountSummary }) {
  const { data } = useQuery({
    queryKey: ['balance-history', account.id, 6],
    queryFn: () => getBalanceHistory(account.id, 6),
  })
  if (!data || data.length < 2) return null
  const owed = account.type === 'CREDIT_CARD'
  const values = data.map((p) => (owed ? Math.abs(p.balance) : p.balance))
  const min = Math.min(...values)
  const span = Math.max(...values) - min || 1
  const points = values
    .map((v, i) => `${(i / (values.length - 1)) * 100},${36 - ((v - min) / span) * 32}`)
    .join(' ')

  return (
    <div aria-hidden="true" className="mt-2" data-testid="balance-preview">
      <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="block h-7 w-full">
        <polyline
          points={points}
          fill="none"
          vectorEffect="non-scaling-stroke"
          style={{ stroke: 'var(--fl-hero-bar)', strokeWidth: 2 }}
        />
      </svg>
      <div className="mt-0.5 flex justify-between text-[10px] text-[var(--fl-hero-fg-2)]">
        <span>{formatShortMonth(data[0].month)}</span>
        <span>{owed ? 'Owed' : 'Balance'}, last 6 months</span>
        <span>{formatShortMonth(data[data.length - 1].month)}</span>
      </div>
    </div>
  )
}
